
import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { InterviewRequestsFacade } from '@/services/interview';
import { InterviewRequestResponse } from '@/services/interview/requests/types';
import { toast } from 'sonner';

export const useRequestDeletion = (onDeleteSuccess?: (deletedId: string) => void) => {
  const [requestToDelete, setRequestToDelete] = useState<string | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const queryClient = useQueryClient();

  const performDelete = async (id: string) => {
    try {
      setIsDeleting(true);
      console.log(`Deleting interview request with ID: ${id}`);
      const result = await InterviewRequestsFacade.deleteInterviewRequest(id);
      
      if (result.success) {
        toast.success('Forespørselen ble slettet');
        
        // Optimistically update the cache
        queryClient.setQueryData(['interviewRequests'], (oldData: InterviewRequestResponse[] | undefined) => {
          if (!oldData) return [];
          const filtered = oldData.filter(req => req.id !== id);
          console.log(`Optimistically filtered request. Before: ${oldData.length}, After: ${filtered.length}`);
          return filtered;
        });
        
        // Notify parent component
        onDeleteSuccess?.(id);
        
        // Invalidate and refetch to ensure consistency
        queryClient.invalidateQueries({ 
          queryKey: ['interviewRequests'],
          refetchType: 'all',
        });
        
        console.log('Deletion workflow completed successfully');
      } else {
        console.error(`Deletion failed with error: ${result.error}`);
        toast.error(`Feil ved sletting: ${result.error || 'Ukjent feil'}`);
      }
    } catch (err) {
      console.error('Feil ved sletting av forespørsel:', err);
      const errorMessage = err instanceof Error ? err.message : 'Ukjent feil';
      toast.error(`En feil oppstod ved sletting: ${errorMessage}`);
    } finally {
      setIsDeleting(false);
      setRequestToDelete(null);
    }
  };

  const handleDeleteRequest = async () => {
    if (!requestToDelete) {
      console.error('No request ID available for deletion');
      return;
    }
    await performDelete(requestToDelete);
  };

  const startDelete = (id: string) => {
    if (!id) {
      console.error('Invalid request ID provided for deletion');
      toast.error('Kunne ikke slette forespørsel. Ugyldig ID.');
      return;
    }
    // For nå: slett direkte uten ekstra bekreftelsesdialog
    setRequestToDelete(id);
    performDelete(id);
  };

  const cancelDelete = () => {
    if (!isDeleting) {
      setRequestToDelete(null);
    }
  };

  return {
    requestToDelete,
    isDeleting,
    handleDeleteRequest,
    startDelete,
    cancelDelete
  };
};
